import { fitCrop, renderTile } from "https://fliesen.grizdesignde.com/geometry.js";
import { xaCropEditor } from "https://fliesen.grizdesignde.com/xa-crop-editor.js";
import { xaButton, xaAlert, xaConfirm, xaDialog, xaElement, xaField, xaIcon, xaNumber, xaSearchControl, xaSelectControl, xaImageFileControl, } from "https://fliesen.grizdesignde.com/xa-elements.js";
import { previewPrice } from "https://fliesen.grizdesignde.com/design-state.js";
import { XaCart } from "https://fliesen.grizdesignde.com/xa-cart.js";
class XaFliesenGenerator extends HTMLElement {
    catalog;
    selected = [];
    config;
    savedRevision = 0;
    timer = 0;
    saving = Promise.resolve();
    status = xaElement("p", "xa-store-status");
    price = xaElement("strong", "xa-store-price", "Preis wird geprüft …");
    root = xaElement("div", "xa-store-generator");
    api = "/api/storefront";
    token = "";
    common = new Map();
    blobUrls = new Map();
    generation = 0;
    savedGeneration = -1;
    unitPrice = 0;
    replacing;
    adding = false;
    conflictCopy = false;
    sizeFields;
    sizeWarning = xaAlert("Diese Größe ist nicht möglich", "");
    cart;
    cartButton = xaButton("In den Warenkorb", () => {
        void this.addToCart();
    }, true);
    retry = xaButton("Speichern erneut versuchen", () => this.changed());
    get storageKey() {
        const variant = new URL(location.href).searchParams.get("variant");
        return `xa-draft-${this.dataset.productId}${variant ? `-variant-${variant}` : ""}`;
    }
    async connectedCallback() {
        if (this.contains(this.root)) {
            return;
        }
        this.api = this.dataset.api || this.api;
        this.append(this.root);
        this.root.addEventListener("input", () => {
            this.updateSizeWarning();
            this.cartButton.disabled =
                this.adding || this.savedGeneration !== this.generation || !this.fieldsValid();
        });
        this.status.setAttribute("role", "status");
        try {
            const stored = localStorage.getItem("xa-session");
            this.token =
                stored ||
                    [...crypto.getRandomValues(new Uint8Array(32))]
                        .map((x) => x.toString(16).padStart(2, "0"))
                        .join("");
            localStorage.setItem("xa-session", this.token);
            this.cart = new XaCart(this.api, this.token);
            this.catalog = await this.request(`/products/${this.dataset.productId}`);
            const editId = new URL(location.href).searchParams.get("xa-edit");
            if (editId) {
                const snapshot = await this.cart.request(`/cart/snapshots/${editId}`);
                if (snapshot.productId !== `gid://shopify/Product/${this.dataset.productId}`) {
                    throw new Error("Die Gestaltung gehört zu einem anderen Artikel.");
                }
                this.replacing = editId;
                this.config = structuredClone(snapshot.configuration);
                this.config.id = crypto.randomUUID();
                this.config.revision = 1;
                this.selected = snapshot.assets.map((x) => ({
                    id: x.motifId || x.fileId,
                    imageId: x.imageId,
                    name: x.name,
                    widthPx: x.widthPx,
                    heightPx: x.heightPx,
                    isUpload: !x.motifId,
                }));
                this.render();
                this.changed();
                return;
            }
            const cached = localStorage.getItem(this.storageKey);
            if (cached) {
                const state = JSON.parse(cached);
                if (state.recoveryConfiguration) {
                    this.config = state.recoveryConfiguration;
                    this.selected = state.selected;
                    this.common = new Map(state.common);
                    this.conflictCopy = true;
                    this.render();
                    this.changed();
                    return;
                }
                const response = await fetch(`${this.api}/drafts/${state.id}`, {
                    headers: { "X-Xa-Session": this.token },
                });
                if (response.ok) {
                    const draft = (await response.json());
                    this.config = draft.configuration;
                    this.savedRevision = this.config.revision;
                    this.selected = state.selected;
                    if (state.revision === this.savedRevision) {
                        this.common = new Map(state.common);
                    }
                    this.render();
                    this.changed();
                    return;
                }
                if (response.status !== 404) {
                    throw new Error("Dein gespeichertes Set konnte nicht geladen werden. Bitte erneut versuchen.");
                }
                localStorage.removeItem(this.storageKey);
            }
            const variant = new URL(location.href).searchParams.get("variant");
            const standard = this.catalog.standards.find((x) => x.shopifyVariantId === `gid://shopify/ProductVariant/${variant}`) || this.catalog.standards[0];
            this.config = {
                id: crypto.randomUUID(),
                revision: 1,
                tileSizeMm: { width: standard.widthMm, height: standard.heightMm },
                images: [],
                tiles: [],
            };
            for (const part of standard.parts) {
                const motif = this.catalog.motifs.find((x) => x.id === part.motifId);
                this.addMotif(motif);
                this.setCount(motif, part.count, false);
            }
            this.render();
            this.changed();
        }
        catch (error) {
            this.root.replaceChildren(xaElement("p", "", error instanceof Error
                ? error.message
                : "Die Gestaltung konnte nicht geladen werden."));
        }
    }
    async request(path, body) {
        const response = await fetch(this.api + path, {
            method: body ? "PUT" : "GET",
            headers: {
                "X-Xa-Session": this.token,
                ...(body ? { "Content-Type": "application/json" } : {}),
            },
            body: body ? JSON.stringify(body) : undefined,
        });
        if (!response.ok) {
            const problem = await response.json().catch(() => ({}));
            throw Object.assign(new Error(problem.title ||
                "Die Gestaltung ist derzeit nicht verfügbar. Bitte erneut versuchen."), { status: response.status });
        }
        return response.json();
    }
    addMotif(motif) {
        if (this.selected.some((x) => x.id === motif.id)) {
            return;
        }
        this.selected.push(motif);
        if (!this.config.images.some((image) => image.id === motif.imageId)) {
            this.config.images.push({
                id: motif.imageId,
                motifId: motif.isUpload ? null : motif.id,
                imageVersionRef: motif.imageId,
                widthPx: motif.widthPx,
                heightPx: motif.heightPx,
            });
        }
    }
    confirmingRemoval = false;
    async changeCount(motif, count) {
        if (this.confirmingRemoval) {
            return;
        }
        if (count !== 0) {
            this.setCount(motif, count);
            return;
        }
        this.confirmingRemoval = true;
        try {
            const remove = await xaConfirm(`„${motif.name}“ wird aus deinem Set entfernt.`, {
                destructive: true,
                detail: motif.isUpload
                    ? "Dein Bild wird aus diesem Set entfernt."
                    : "Die Motivbibliothek bleibt unverändert.",
            });
            if (remove) {
                this.selected = this.selected.filter((item) => item.id !== motif.id);
                this.config.tiles = this.config.tiles.filter((item) => item.imageId !== motif.imageId);
                this.config.images = this.config.images.filter((item) => item.id !== motif.imageId);
                this.common.delete(motif.imageId);
                this.refreshQuantities();
                this.changed();
            }
            else {
                this.setCount(motif, 1);
            }
        }
        finally {
            this.confirmingRemoval = false;
        }
    }
    setCount(motif, count, refresh = true) {
        const current = this.config.tiles.filter((x) => x.imageId === motif.imageId);
        if (!Number.isInteger(count) ||
            count < 0 ||
            this.config.tiles.length - current.length + count >
                this.catalog.limits.maximumTileCount)
            return;
        if (count < current.length) {
            const removed = new Set(current.slice(count).map((tile) => tile.id));
            this.config.tiles = this.config.tiles.filter((tile) => !removed.has(tile.id));
        }
        else {
            const template = this.common.get(motif.imageId);
            for (let i = current.length; i < count; i++) {
                this.config.tiles.push({
                    id: crypto.randomUUID(),
                    imageId: motif.imageId,
                    rotationDeg: template?.rotationDeg || 0,
                    crop: structuredClone(template?.crop ||
                        fitCrop(motif.widthPx, motif.heightPx, this.config.tileSizeMm.width, this.config.tileSizeMm.height, 0)),
                });
            }
        }
        if (refresh) {
            this.refreshQuantities();
            this.changed();
        }
    }
    refreshQuantities() {
        const rows = Array.from(this.root.querySelectorAll(".xa-store-row"));
        if (this.selected.some((motif) => !rows.some((row) => row.dataset.imageId === motif.imageId))) {
            this.render();
            return;
        }
        const total = this.config.tiles.length;
        for (const row of rows) {
            if (!this.selected.some((motif) => motif.imageId === row.dataset.imageId)) {
                row.remove();
                continue;
            }
            const count = this.config.tiles.filter((tile) => tile.imageId === row.dataset.imageId).length;
            row.querySelector(".xa-store-quantity input").value = String(count);
            row.querySelector('[data-quantity-action="minus"]').disabled = count === 0;
            row.querySelector('[data-quantity-action="plus"]').disabled =
                total >= this.catalog.limits.maximumTileCount;
            const hint = row.querySelector(".xa-store-count-hint");
            if (hint) {
                hint.hidden = count !== 0;
            }
        }
        const motifCount = this.root.querySelector(".xa-store-motif-count");
        if (motifCount) {
            motifCount.textContent = `${this.selected.length} Motive`;
        }
        const size = this.config.tileSizeMm;
        this.root.querySelectorAll(".xa-store-summary").forEach((summary) => {
            summary.firstChild.textContent = `${total} Aufkleber · ${size.width} × ${size.height} mm`;
            if (!summary.classList.contains("xa-store-top-summary")) {
                summary.querySelector("small").textContent = this.selected.map((motif) => {
                    const count = this.config.tiles.filter((tile) => tile.imageId === motif.imageId).length;
                    return count ? `${count} × ${motif.name}` : "";
                }).filter(Boolean).join(", ");
            }
        });
    }
    render() {
        // Rebuilding rows must not move the viewport or discard keyboard focus.
        // Keep the layout height while persistent price/cart nodes are reparented.
        const populated = this.root.childElementCount > 0;
        const left = window.scrollX;
        const top = window.scrollY;
        const active = document.activeElement;
        const focusKey = active && this.root.contains(active) ? active.dataset.xaFocusKey : undefined;
        const modeSwitch = this.root.querySelector(":scope > [data-xa-switch]");
        const minHeight = this.root.style.minHeight;
        const overflowAnchor = this.root.style.overflowAnchor;
        if (populated) {
            this.root.style.minHeight = `${this.root.getBoundingClientRect().height}px`;
            this.root.style.overflowAnchor = "none";
        }
        try {
            this.renderContent(modeSwitch);
        }
        finally {
            this.root.style.minHeight = minHeight;
            if (focusKey) {
                Array.from(this.root.querySelectorAll("[data-xa-focus-key]"))
                    .find((element) => element.dataset.xaFocusKey === focusKey)
                    ?.focus({ preventScroll: true });
            }
            if (populated && (window.scrollX !== left || window.scrollY !== top)) {
                window.scrollTo({ left, top, behavior: "instant" });
            }
            this.root.style.overflowAnchor = overflowAnchor;
        }
    }
    renderContent(modeSwitch) {
        const compact = this.dataset.presentation === "product-modes";
        // Older drafts use the previously displayed first instance as the common crop.
        for (const motif of this.selected) {
            const first = this.config.tiles.find((item) => item.imageId === motif.imageId);
            if (!first)
                continue;
            this.common.set(motif.imageId, structuredClone(first));
            this.config.tiles = this.config.tiles.map((item) => item.imageId === motif.imageId
                ? { ...item, rotationDeg: first.rotationDeg, crop: structuredClone(first.crop) }
                : item);
        }
        const limits = this.catalog.limits;
        const size = this.config.tileSizeMm;
        const fields = xaElement("div", "xa-store-size");
        this.sizeFields = fields;
        this.sizeWarning.id ||= `xa-size-warning-${crypto.randomUUID()}`;
        this.sizeWarning.hidden = true;
        fields.append(xaElement("h3", "xa-store-mobile-only xa-store-size-title", "Größe der Aufkleber"));
        fields.append(xaNumber("Breite (mm)", size.width, limits.minimumWidthMm, limits.maximumWidthMm, (value) => this.resize(value, size.height)), xaNumber("Höhe (mm)", size.height, limits.minimumHeightMm, limits.maximumHeightMm, (value) => this.resize(size.width, value)));
        fields.append(this.sizeWarning, xaElement("p", "xa-store-hint", `Maximal ${limits.maximumMotifCount} Motive und ${limits.maximumTileCount} Aufkleber pro Bestellung.`));
        fields.querySelectorAll("input").forEach((input, index) => {
            input.dataset.xaFocusKey = `size-${index}`;
        });
        const rows = xaElement("div", "xa-store-rows");
        rows.append(xaElement("h3", "", "Motive im Set"));
        if (compact) {
            rows.append(xaElement("small", "xa-store-motif-count", `${this.selected.length} Motive`));
        }
        for (const motif of this.selected) {
            const motifTiles = this.config.tiles.filter((x) => x.imageId === motif.imageId);
            const count = motifTiles.length;
            const row = xaElement("div", "xa-store-row");
            row.dataset.imageId = motif.imageId;
            const previewTile = motifTiles[0] ||
                this.common.get(motif.imageId) || {
                id: "common",
                imageId: motif.imageId,
                rotationDeg: 0,
                crop: fitCrop(motif.widthPx, motif.heightPx, size.width, size.height, 0),
            };
            const image = xaElement("canvas", "xa-store-crop-preview");
            image.width = 168;
            image.height = Math.round((168 * size.height) / size.width);
            image.setAttribute("role", "img");
            image.setAttribute("aria-label", motif.name + ": zugeschnittener Ausschnitt");
            const source = new Image();
            source.onload = () => renderTile(image, source, previewTile);
            // Normalized crops and rotations also work with the smaller preview image.
            this.setImage(source, motif, "thumbnail");
            const name = xaElement("div", "xa-store-name");
            const mobileName = xaButton("", () => this.edit(motif));
            mobileName.append(xaElement("span", "xa-store-motif-title", motif.name), xaElement("small", "xa-store-motif-size", `${size.width} × ${size.height} mm`));
            mobileName.classList.add("xa-store-motif-name-action");
            mobileName.setAttribute("aria-label", motif.name + ": Bearbeiten über Titel");
            name.append(mobileName);
            const imageAction = xaButton("", () => this.edit(motif));
            imageAction.classList.add("xa-store-image-action");
            imageAction.setAttribute("aria-label", motif.name + ": Bearbeiten über Bild");
            imageAction.append(image);
            const quantity = xaElement("div", "xa-store-quantity");
            const minus = xaButton("−", () => {
                void this.changeCount(motif, this.config.tiles.filter((tile) => tile.imageId === motif.imageId).length - 1);
            });
            minus.dataset.quantityAction = "minus";
            minus.setAttribute("aria-label", `${motif.name}: Anzahl verringern`);
            const plus = xaButton("+", () => {
                void this.changeCount(motif, this.config.tiles.filter((tile) => tile.imageId === motif.imageId).length + 1);
            });
            plus.dataset.quantityAction = "plus";
            plus.setAttribute("aria-label", `${motif.name}: Anzahl erhöhen`);
            minus.disabled = count === 0;
            plus.disabled = this.config.tiles.length >= limits.maximumTileCount;
            quantity.append(minus, xaNumber("Anzahl", count, 0, limits.maximumTileCount, (value) => {
                void this.changeCount(motif, value);
            }), plus);
            const quantityInput = quantity.querySelector("input");
            quantityInput.addEventListener("input", () => {
                if (quantityInput.value !== "" && quantityInput.valueAsNumber === 0) {
                    void this.changeCount(motif, 0);
                }
            });
            row.append(imageAction, name, quantity);
            row.querySelectorAll("button, input").forEach((control, index) => {
                control.dataset.xaFocusKey = `motif-${motif.id}-${index}`;
            });
            rows.append(row);
            if (count === 0) {
                name.append(xaElement("small", "xa-store-count-hint", "Anzahl wählen"));
            }
        }
        const more = xaElement("div", "xa-store-more");
        more.append(xaButton(compact ? "Motive wählen" : "Weitere Motive wählen", () => {
            void this.library();
        }));
        more.append(xaButton(compact ? "Eigenes Bild" : "Eigenes Bild hochladen", () => this.upload(more)));
        more.children[0].prepend(xaIcon("library"));
        more.children[1].prepend(xaIcon("picture"));
        ["Aus unserer Designauswahl", "JPG/PNG · bis 20 MB"].forEach((subtitle, index) => {
            const button = more.children[index];
            const label = button.lastChild;
            button.setAttribute("aria-label", label.textContent);
            const text = xaElement("span", "xa-store-action-label");
            text.append(label, xaElement("small", "xa-store-action-subtitle", subtitle));
            button.append(text);
        });
        const summary = xaElement("div", "xa-store-summary", `${this.config.tiles.length} Aufkleber · ${size.width} × ${size.height} mm`);
        summary.append(xaElement("small", "", this.selected
            .map((motif) => {
            const count = this.config.tiles.filter((x) => x.imageId === motif.imageId).length;
            return count ? `${count} × ${motif.name}` : "";
        })
            .filter(Boolean)
            .join(", ")));
        const offer = xaElement("div", "xa-store-offer");
        const pricing = xaElement("div", "xa-store-pricing");
        const tax = xaElement("p", "xa-store-tax", "inkl. MwSt., zzgl. ");
        const shipping = xaElement("a", "", "Versand");
        shipping.href = "/policies/shipping-policy";
        tax.append(shipping);
        const minimum = xaElement("small", "xa-store-minimum", `Mindestpreis: ${Math.min(this.catalog.priceRule.minimumPrice, ...this.catalog.standards.map((standard) => standard.price)).toLocaleString("de-DE", { style: "currency", currency: this.catalog.priceRule.currency })}`);
        pricing.append(this.price, tax, minimum);
        const topSummary = summary.cloneNode(true);
        topSummary.classList.add("xa-store-top-summary");
        topSummary.querySelector("small").textContent =
            "Individuell gestaltet. Einzigartig wie dein Zuhause.";
        offer.append(pricing, topSummary);
        const badge = xaElement("div", "xa-store-custom-badge");
        badge.append(xaIcon("leaf"), xaElement("span", "", "Individuell gestalten"));
        offer.append(badge);
        const preview = xaButton("Set-Vorschau ansehen", () => this.preview());
        preview.prepend(xaIcon("preview"));
        preview.classList.add("xa-store-preview");
        const actions = xaElement("div", "xa-store-actions");
        this.cartButton.classList.add("xa-store-add-cart");
        this.cartButton.textContent = this.replacing
            ? "Änderungen in den Warenkorb übernehmen"
            : "In den Warenkorb";
        this.cartButton.prepend(xaIcon("cart"));
        const sticky = xaElement("div", "xa-store-cart-action");
        const stickyPrice = xaElement("strong", "xa-store-sticky-price", this.price.textContent?.split(" · ")[0] || "Preis wird geprüft …");
        const stickyDetails = xaElement("div", "xa-store-sticky-details");
        stickyDetails.append(stickyPrice, tax.cloneNode(true), minimum.cloneNode(true));
        sticky.append(stickyDetails, this.cartButton);
        actions.append(preview, sticky);
        this.root.replaceChildren(offer, ...(modeSwitch ? [modeSwitch] : []), xaElement("h2", "xa-store-desktop-only", "Dein Fliesenset gestalten"), xaElement("h2", "xa-store-mobile-only", compact ? "Dein Fliesenset gestalten" : "Dein individuelles Set"), xaElement("p", "xa-store-desktop-only", compact ? "Maße, Motive und Mengen selbst anpassen." : "Wähle deine Motive, lege die Größe fest und gestalte dein individuelles Set."), xaElement("p", "xa-store-mobile-only xa-store-introduction", compact ? "Maße, Motive und Mengen selbst anpassen." : "Wähle deine Motive und lege die Anzahl pro Design fest."), fields, rows, more, xaElement("p", "xa-store-hint", "Deine Originalbilder sind geschützt. Prüfe deine Bilder und Ausschnitte über „Set-Vorschau ansehen“, bevor du dein Set in den Warenkorb legst."), summary, actions, this.status, this.retry);
        this.retry.hidden = true;
    }
    resize(width, height) {
        const sizeBefore = this.config.tileSizeMm;
        this.config.tileSizeMm = { width, height };
        const resized = (tile) => {
            const image = this.config.images.find((x) => x.id === tile.imageId);
            const previousFit = fitCrop(image.widthPx, image.heightPx, sizeBefore.width, sizeBefore.height, tile.rotationDeg);
            const zoom = previousFit.width / tile.crop.width;
            return {
                ...tile,
                crop: fitCrop(image.widthPx, image.heightPx, width, height, tile.rotationDeg, tile.crop.x + tile.crop.width / 2, tile.crop.y + tile.crop.height / 2, zoom),
            };
        };
        this.config.tiles = this.config.tiles.map(resized);
        this.common.forEach((tile, imageId) => this.common.set(imageId, resized(tile)));
        this.render();
        this.changed();
    }
    setImage(image, motif, variant) {
        if (!motif.isUpload) {
            image.crossOrigin = "anonymous";
            image.src = `${this.api}/images/${motif.imageId}/${variant}`;
            return;
        }
        const key = `${motif.imageId}/${variant}`;
        const known = this.blobUrls.get(key);
        if (known) {
            image.src = known;
            return;
        }
        void fetch(`${this.api}/images/${key}`, { headers: { "X-Xa-Session": this.token } })
            .then(async (response) => {
            if (!response.ok) {
                throw new Error("Eigenes Bild kann nicht geladen werden.");
            }
            const url = URL.createObjectURL(await response.blob());
            this.blobUrls.set(key, url);
            image.src = url;
        })
            .catch((error) => {
            this.status.textContent = String(error);
        });
    }
    upload(anchor) {
        if (this.selected.length >= this.catalog.limits.maximumMotifCount) {
            this.status.textContent = `Höchstens ${this.catalog.limits.maximumMotifCount} unterschiedliche Motive pro Gestaltung. Bitte zuerst ein Motiv entfernen oder tauschen.`;
            return;
        }
        anchor.parentElement.querySelector(".xa-store-upload")?.remove();
        const panel = xaElement("div", "xa-store-upload");
        panel.hidden = true;
        anchor.after(panel);
        const uploadButton = anchor.children[1];
        const file = xaImageFileControl();
        const progress = xaElement("progress");
        progress.max = 100;
        progress.value = 0;
        progress.setAttribute("aria-label", "Uploadfortschritt");
        const message = xaElement("p", "", "JPG oder PNG, höchstens 20 MB und 40 Megapixel. Die Originaldatei wird geschützt gespeichert.");
        message.setAttribute("role", "status");
        let request;
        file.addEventListener("change", () => {
            const selected = file.files?.[0];
            if (!selected) {
                return;
            }
            panel.hidden = false;
            if (selected.size > 20_000_000) {
                progress.hidden = true;
                message.textContent = "Bitte eine Datei mit höchstens 20 MB wählen.";
                return;
            }
            request = new XMLHttpRequest();
            request.open("POST", `${this.api}/uploads`);
            request.setRequestHeader("X-Xa-Session", this.token);
            request.setRequestHeader("Content-Type", "application/octet-stream");
            request.upload.onprogress = (event) => {
                progress.value = event.lengthComputable ? (event.loaded / event.total) * 100 : 0;
                message.textContent =
                    progress.value < 100
                        ? "Bild wird hochgeladen …"
                        : "Bild wird geprüft und vorbereitet …";
            };
            request.onload = () => {
                file.disabled = false;
                uploadButton.disabled = false;
                try {
                    const result = JSON.parse(request.responseText);
                    if (request.status !== 200) {
                        throw new Error(result.title || "Upload fehlgeschlagen.");
                    }
                    if (this.config.tiles.length >= this.catalog.limits.maximumTileCount ||
                        this.selected.length >= this.catalog.limits.maximumMotifCount) {
                        throw new Error("Die maximale Anzahl Aufkleber oder unterschiedlicher Motive ist erreicht. Bitte zuerst ein Motiv entfernen oder eine Menge verringern.");
                    }
                    this.addMotif(result);
                    this.setCount(result, 1, false);
                    panel.remove();
                    this.render();
                    this.changed();
                }
                catch (error) {
                    progress.hidden = true;
                    message.textContent = String(error);
                }
            };
            request.onerror = () => {
                file.disabled = false;
                uploadButton.disabled = false;
                progress.hidden = true;
                message.textContent = "Upload unterbrochen. Bitte erneut versuchen.";
            };
            file.disabled = true;
            uploadButton.disabled = true;
            message.textContent = `${selected.name} wird hochgeladen …`;
            request.send(selected);
        });
        const cancel = xaButton("Abbrechen", () => {
            request?.abort();
            uploadButton.disabled = false;
            panel.remove();
        });
        file.addEventListener("cancel", () => panel.remove());
        panel.append(message, file, progress, cancel);
        file.click();
    }
    preview() {
        const { dialog, body, footer } = xaDialog("Deine Setvorschau");
        const grid = xaElement("div", "xa-store-library");
        body.append(xaElement("p", "", `${this.config.tiles.length} Aufkleber · ${this.config.tileSizeMm.width} × ${this.config.tileSizeMm.height} mm`), grid);
        const images = new Map();
        for (const tile of this.config.tiles) {
            const motif = this.selected.find((item) => item.imageId === tile.imageId);
            const card = xaElement("div", "xa-store-preview-card");
            const canvas = xaElement("canvas");
            canvas.width = 240;
            canvas.height = (240 * this.config.tileSizeMm.height) / this.config.tileSizeMm.width;
            canvas.setAttribute("aria-label", `Aufkleber ${grid.children.length + 1}`);
            const actions = xaElement("div", "xa-store-preview-card-actions");
            const remove = xaButton("", () => {
                dialog.close();
                void (async () => {
                    const count = this.config.tiles.filter((item) => item.imageId === motif.imageId).length;
                    const accepted = await xaConfirm(`Ein Aufkleber mit „${motif.name}“ wird aus deinem Set entfernt.`, {
                        destructive: true,
                        title: "Aufkleber löschen?",
                        detail: count > 1
                            ? `Die übrigen ${count - 1} Aufkleber dieses Motivs bleiben erhalten.`
                            : "Dies ist der letzte Aufkleber dieses Motivs im Set.",
                    });
                    if (accepted) {
                        this.setCount(motif, count - 1, false);
                        if (count === 1) {
                            this.selected = this.selected.filter((item) => item.id !== motif.id);
                            this.config.images = this.config.images.filter((item) => item.id !== motif.imageId);
                            this.common.delete(motif.imageId);
                        }
                        this.render();
                        this.changed();
                    }
                    this.preview();
                })();
            });
            remove.classList.add("xa-store-preview-delete");
            remove.setAttribute("aria-label", `${motif.name}: Aufkleber löschen`);
            remove.title = "Löschen";
            remove.append(xaIcon("trash"));
            const edit = xaButton("", () => {
                dialog.close();
                this.edit(motif, () => this.preview());
            });
            edit.setAttribute("aria-label", `${motif.name}: Motiv bearbeiten`);
            edit.title = "Bearbeiten";
            edit.append(xaIcon("edit"));
            actions.append(remove, edit);
            card.append(canvas, actions);
            grid.append(card);
            if (!images.has(tile.imageId)) {
                images.set(tile.imageId, new Promise((resolve, reject) => {
                    const image = new Image();
                    image.onload = () => resolve(image);
                    image.onerror = () => reject(new Error("Ein Vorschaubild konnte nicht geladen werden."));
                    this.setImage(image, this.selected.find((motif) => motif.imageId === tile.imageId), "thumbnail");
                }));
            }
            void images
                .get(tile.imageId)
                .then((image) => {
                if (dialog.isConnected) {
                    renderTile(canvas, image, tile);
                }
            })
                .catch((error) => {
                canvas.replaceWith(xaElement("p", "", String(error)));
            });
        }
        footer.append(xaButton("Schließen", () => dialog.close()));
    }
    changed() {
        const generation = ++this.generation;
        if (this.catalog && this.config.tiles.length) {
            this.showPrice(previewPrice(this.config, this.catalog.standards, this.catalog.priceRule), this.catalog.priceRule.isTest);
        }
        this.cartButton.disabled = true;
        this.status.textContent = this.config.tiles.length
            ? "Änderungen werden gespeichert …"
            : "Bitte mindestens einen Aufkleber auswählen.";
        window.clearTimeout(this.timer);
        this.retry.hidden = true;
        if (!this.config.tiles.length) {
            return;
        }
        this.timer = window.setTimeout(() => {
            this.saving = this.saving
                .then(async () => {
                const configuration = structuredClone(this.config);
                const presentation = {
                    selected: structuredClone(this.selected),
                    common: [...this.common],
                };
                configuration.revision = this.savedRevision + 1;
                const save = () => this.request(`/drafts/${configuration.id}`, {
                    productId: `gid://shopify/Product/${this.dataset.productId}`,
                    configuration,
                    expectedRevision: this.savedRevision,
                });
                let result;
                try {
                    result = await save();
                }
                catch (error) {
                    if (!(error instanceof Error) ||
                        !("status" in error) ||
                        error.status !== 409) {
                        throw error;
                    }
                    // Keep the other tab's server draft. Preserve our current edits under a new ID,
                    // including a reload recovery copy before attempting the new request.
                    const id = crypto.randomUUID();
                    this.config.id = id;
                    this.savedRevision = 0;
                    configuration.id = id;
                    configuration.revision = 1;
                    this.conflictCopy = true;
                    localStorage.setItem(this.storageKey, JSON.stringify({
                        id,
                        revision: 0,
                        selected: this.selected,
                        common: [...this.common],
                        recoveryConfiguration: this.config,
                    }));
                    result = await save();
                }
                this.savedRevision = result.configuration.revision;
                localStorage.setItem(this.storageKey, JSON.stringify({
                    id: configuration.id,
                    revision: this.savedRevision,
                    ...presentation,
                }));
                if (generation === this.generation) {
                    this.showPrice(result.quote.unitPrice, result.quote.isTest);
                    const sku = this.closest(".xa-product-content")?.querySelector(".xa-product-sku");
                    if (sku) {
                        sku.textContent = `SKU: ${result.quote.articleNumber}`;
                    }
                    this.unitPrice = result.quote.unitPrice;
                    this.savedGeneration = generation;
                    this.cartButton.disabled = this.adding || !this.fieldsValid();
                    this.status.textContent = this.conflictCopy
                        ? "Konflikt mit einem anderen Tab: Deine Gestaltung wurde als eigene Kopie gespeichert. Der andere Stand bleibt erhalten."
                        : this.replacing
                            ? "Gespeichert. Änderungen mit dem Warenkorb-Button übernehmen."
                            : "Gespeichert. Noch nicht im Warenkorb.";
                }
            })
                .catch((error) => {
                this.status.textContent =
                    error instanceof Error ? error.message : "Speichern fehlgeschlagen.";
                this.retry.hidden = false;
            });
        }, 500);
    }
    showPrice(value, isTest) {
        this.price.textContent =
            value.toLocaleString("de-DE", {
                style: "currency",
                currency: this.catalog?.priceRule.currency || "EUR",
            }) + (isTest ? " · Testpreis" : "");
        const stickyPrice = this.root.querySelector(".xa-store-sticky-price");
        if (stickyPrice) {
            stickyPrice.textContent = value.toLocaleString("de-DE", {
                style: "currency",
                currency: this.catalog?.priceRule.currency || "EUR",
            });
        }
    }
    async addToCart() {
        if (!this.fieldsValid(true)) {
            this.cartButton.disabled = true;
            this.status.textContent = "Bitte die markierten Eingaben korrigieren.";
            return;
        }
        if (this.adding || this.savedGeneration !== this.generation || !this.config.tiles.length) {
            return;
        }
        this.adding = true;
        const generation = this.generation;
        this.cartButton.disabled = true;
        this.status.textContent = "Dein Set wird geprüft …";
        try {
            const snapshot = await this.cart.request("/cart/snapshots", {
                draftId: this.config.id,
                revision: this.savedRevision,
                expectedPrice: this.unitPrice,
            });
            if (generation !== this.generation || !this.fieldsValid(true)) {
                this.status.textContent =
                    "Deine Gestaltung wurde inzwischen geändert. Bitte den aktuellen Stand übernehmen.";
                return;
            }
            await this.cart.add(snapshot, this.replacing);
            this.replacing = snapshot.id;
            this.cartButton.textContent = "Änderungen in den Warenkorb übernehmen";
            this.status.textContent = "Dein Set liegt im Warenkorb.";
            await this.cart.open();
        }
        catch (error) {
            this.status.textContent =
                error instanceof Error ? error.message : "Das Set konnte nicht übernommen werden.";
        }
        finally {
            this.adding = false;
            this.cartButton.disabled =
                this.savedGeneration !== this.generation || !this.fieldsValid();
        }
    }
    fieldsValid(report = false) {
        this.updateSizeWarning();
        const invalid = Array.from(this.root.querySelectorAll("input")).find((input) => !input.checkValidity());
        if (report) {
            invalid?.reportValidity();
        }
        return !invalid;
    }
    updateSizeWarning() {
        if (!this.sizeFields) {
            return;
        }
        const messages = Array.from(this.sizeFields.querySelectorAll("input")).flatMap((input, index) => {
            const valid = input.checkValidity();
            input.setAttribute("aria-invalid", String(!valid));
            input.setAttribute("aria-describedby", this.sizeWarning.id);
            if (valid) {
                return [];
            }
            const dimension = index === 0 ? "Breite" : "Höhe";
            const whole = input.validity.stepMismatch ? " in ganzen Millimetern" : "";
            return [
                `Bitte gib eine ${dimension} zwischen ${input.min} und ${input.max} mm${whole} ein.`,
            ];
        });
        this.sizeWarning.querySelector("p").textContent = messages.join(" ");
        this.sizeWarning.hidden = messages.length === 0;
    }
    async library(replace, chosen, host) {
        const { dialog, body, footer } = host ||
            xaDialog(replace ? "Motiv tauschen" : "Weitere Motive wählen", {
                closeOnBackdrop: true,
            });
        dialog.classList.add("xa-store-library-dialog");
        let searchTimer = 0;
        const closeLibrary = () => {
            clearTimeout(searchTimer);
            dialog.classList.remove("xa-store-library-dialog");
            if (host) {
                host.restore();
            }
            else {
                dialog.close();
            }
        };
        const description = xaElement("p", "xa-store-library-description", replace
            ? `Wähle ein neues Motiv für „${replace.name}“. Die Anzahl bleibt erhalten.`
            : "Wähle Motive aus, die du deinem Set hinzufügen möchtest.");
        const grid = xaElement("div", "xa-store-library");
        const toolbar = xaElement("div", "xa-store-library-toolbar");
        const searchWrap = xaElement("div", "xa-store-library-search");
        const search = xaSearchControl("Motive suchen …");
        searchWrap.append(xaIcon("search"), search);
        const filterPanel = xaElement("div", "xa-store-library-filters");
        filterPanel.hidden = true;
        filterPanel.id = `xa-filter-${crypto.randomUUID()}`;
        const filterButton = xaButton("Filter", () => {
            filterPanel.hidden = !filterPanel.hidden;
            filterButton.setAttribute("aria-expanded", String(!filterPanel.hidden));
            if (!filterPanel.hidden) {
                filter.focus();
            }
        });
        filterButton.prepend(xaIcon("filter"));
        filterButton.setAttribute("aria-expanded", "false");
        filterButton.setAttribute("aria-controls", filterPanel.id);
        toolbar.append(searchWrap, filterButton);
        const filter = xaSelectControl();
        const all = xaElement("option", "", "Alle Merkmale");
        all.value = "";
        filter.append(all);
        filter.disabled = true;
        filterPanel.append(xaField("Nach Merkmal filtern", filter));
        void this.request("/facets")
            .then((facets) => {
            for (const facet of facets) {
                const option = xaElement("option", "", facet.name);
                option.value = facet.id;
                filter.append(option);
            }
            filter.disabled = false;
        })
            .catch(() => {
            all.textContent = "Merkmale derzeit nicht verfügbar";
        });
        let generation = 0;
        let number = 1;
        let hideUsed = false;
        let currentItems = [];
        let total = 0;
        const selection = new Map();
        const metadata = xaElement("div", "xa-store-library-metadata");
        const results = xaElement("span");
        results.setAttribute("role", "status");
        const hideButton = xaButton("Bereits im Set ausblenden", () => {
            hideUsed = !hideUsed;
            hideButton.setAttribute("aria-pressed", String(hideUsed));
            renderCards();
        });
        hideButton.classList.add("xa-store-library-hide");
        hideButton.setAttribute("aria-pressed", "false");
        metadata.append(results, hideButton);
        const count = xaElement("span", "xa-store-library-selection");
        count.setAttribute("role", "status");
        const accept = xaButton("", () => {
            if (!selection.size) {
                return;
            }
            if (replace) {
                chosen?.(selection.values().next().value);
                closeLibrary();
            }
            else {
                if (this.config.tiles.length + selection.size >
                    this.catalog.limits.maximumTileCount ||
                    this.selected.length + selection.size >
                        this.catalog.limits.maximumMotifCount) {
                    return;
                }
                selection.forEach((motif) => {
                    this.addMotif(motif);
                    this.setCount(motif, 1, false);
                });
                closeLibrary();
                this.render();
                this.changed();
            }
        }, true);
        const updateSelection = () => {
            count.textContent =
                selection.size === 0
                    ? replace
                        ? "Noch kein Motiv ausgewählt"
                        : "Noch keine Motive ausgewählt"
                    : `${selection.size} ${selection.size === 1 ? "Motiv" : "Motive"} ausgewählt`;
            accept.textContent = replace
                ? "Motiv tauschen"
                : `${selection.size} ${selection.size === 1 ? "Motiv hinzufügen" : "Motive hinzufügen"}`;
            const exceedsLimit = !replace &&
                (this.config.tiles.length + selection.size >
                    this.catalog.limits.maximumTileCount ||
                    this.selected.length + selection.size > this.catalog.limits.maximumMotifCount);
            if (exceedsLimit) {
                count.textContent = `Höchstens ${this.catalog.limits.maximumMotifCount} unterschiedliche Motive und ${this.catalog.limits.maximumTileCount} Aufkleber. Bitte weniger Motive auswählen.`;
            }
            accept.disabled = selection.size === 0 || exceedsLimit;
        };
        const renderCards = () => {
            grid.replaceChildren();
            const visible = currentItems.filter((motif) => !hideUsed || !this.selected.some((item) => item.id === motif.id));
            results.textContent = hideUsed
                ? `${visible.length} ${visible.length === 1 ? "Motiv" : "Motive"} auf dieser Seite`
                : `${total} ${total === 1 ? "Motiv" : "Motive"}`;
            for (const motif of visible) {
                const used = this.selected.some((item) => item.id === motif.id);
                const card = xaButton("", () => {
                    if (selection.has(motif.id)) {
                        selection.delete(motif.id);
                    }
                    else {
                        if (replace) {
                            selection.clear();
                        }
                        selection.set(motif.id, motif);
                    }
                    renderCards();
                    updateSelection();
                    grid.querySelector(`[data-motif-id="${motif.id}"]`)?.focus();
                });
                card.classList.add("xa-store-library-card");
                card.dataset.motifId = motif.id;
                card.disabled = used;
                card.setAttribute("aria-label", `${motif.name}${used ? ", bereits im Set" : ""}`);
                const image = xaElement("img");
                image.src = `${this.api}/images/${motif.imageId}/thumbnail`;
                image.alt = "";
                image.loading = "lazy";
                card.append(image, xaElement("span", "xa-store-library-name", motif.name));
                if (used) {
                    card.append(xaElement("span", "xa-store-library-used", "Bereits im Set"));
                }
                else {
                    const selected = selection.has(motif.id);
                    card.setAttribute("aria-pressed", String(selected));
                    const mark = xaElement("span", "xa-store-library-check", selected ? "✓" : "");
                    mark.setAttribute("aria-hidden", "true");
                    card.append(mark);
                }
                grid.append(card);
            }
            if (!visible.length) {
                grid.append(xaElement("p", "xa-store-library-empty", total
                    ? "Alle Motive auf dieser Seite sind bereits im Set. Blende sie wieder ein oder gehe zur nächsten Seite."
                    : "Keine Motive gefunden. Ändere deine Suche oder den Filter."));
            }
        };
        const pagination = xaElement("div", "xa-store-pagination");
        const load = async () => {
            const own = ++generation;
            grid.setAttribute("aria-busy", "true");
            try {
                const page = await this.request(`/motifs?search=${encodeURIComponent(search.value)}&page=${number}${filter.value ? `&facetId=${filter.value}` : ""}`);
                if (own !== generation ||
                    !dialog.isConnected ||
                    !dialog.classList.contains("xa-store-library-dialog")) {
                    return;
                }
                currentItems = page.items;
                total = page.total;
                renderCards();
                const previous = xaButton("Zurück", () => {
                    number--;
                    void load();
                });
                const next = xaButton("Weiter", () => {
                    number++;
                    void load();
                });
                previous.disabled = number === 1;
                next.disabled = number * page.pageSize >= page.total;
                pagination.replaceChildren(previous, xaElement("span", "", `Seite ${number} von ${Math.max(1, Math.ceil(page.total / page.pageSize))}`), next);
                pagination.hidden = page.total <= page.pageSize;
            }
            catch {
                if (own === generation) {
                    grid.replaceChildren(xaElement("p", "xa-store-library-empty", "Motive konnten nicht geladen werden. Bitte versuche es erneut."), xaButton("Erneut laden", () => {
                        void load();
                    }));
                }
            }
            finally {
                if (own === generation) {
                    grid.removeAttribute("aria-busy");
                }
            }
        };
        search.addEventListener("input", () => {
            clearTimeout(searchTimer);
            searchTimer = window.setTimeout(() => {
                number = 1;
                void load();
            }, 300);
        });
        filter.addEventListener("change", () => {
            number = 1;
            filterButton.lastChild.textContent = filter.value ? "Filter (1)" : "Filter";
            void load();
        });
        body.append(description, toolbar, filterPanel, metadata, grid, pagination);
        footer.append(count, xaButton("Abbrechen", closeLibrary), accept);
        updateSelection();
        await load();
    }
    edit(motif, closed) {
        let tiles = this.config.tiles.filter((x) => x.imageId === motif.imageId);
        let originalMotif = motif;
        const sessions = new Map();
        const { dialog, body, footer } = xaDialog("Ausschnitt anpassen", { closeOnBackdrop: true });
        if (closed) {
            dialog.addEventListener("close", closed, { once: true });
        }
        dialog.classList.add("xa-store-crop-dialog");
        const header = dialog.querySelector("header");
        const title = header.querySelector("h2");
        const headerText = xaElement("div", "xa-store-crop-header-text");
        title.replaceWith(headerText);
        headerText.append(title);
        let workingMotif = motif;
        let workingTiles = structuredClone(tiles);
        let common = structuredClone(this.common.get(motif.imageId) || {
            id: "common",
            imageId: motif.imageId,
            rotationDeg: 0,
            crop: fitCrop(motif.widthPx, motif.heightPx, this.config.tileSizeMm.width, this.config.tileSizeMm.height, 0),
        });
        let tile = structuredClone(workingTiles[0] || common);
        const saveCurrent = () => {
            common = structuredClone(tile);
            workingTiles = workingTiles.map((item) => ({
                ...item,
                imageId: workingMotif.imageId,
                rotationDeg: tile.rotationDeg,
                crop: structuredClone(tile.crop),
            }));
        };
        const persistMotif = () => {
            saveCurrent();
            sessions.set(originalMotif.id, {
                original: originalMotif,
                motif: workingMotif,
                tiles: structuredClone(workingTiles),
                common: structuredClone(common),
            });
        };
        const image = new Image();
        const editor = xaCropEditor(image, () => tile, this.config.tileSizeMm.width / this.config.tileSizeMm.height, (crop) => {
            tile.crop = crop;
            draw();
        });
        let ready = false;
        const subtitle = xaElement("p", "xa-store-crop-subtitle", `${motif.name} · ${tiles.length} Aufkleber`);
        const motifList = xaElement("div", "xa-store-crop-motifs");
        const motifTrack = xaElement("div", "xa-store-crop-motif-track");
        const previousMotifs = xaButton("‹", () => {
            motifTrack.scrollBy({ left: -240, behavior: "smooth" });
        });
        previousMotifs.classList.add("xa-store-crop-motif-nav");
        previousMotifs.setAttribute("aria-label", "Vorherige Motive anzeigen");
        const nextMotifs = xaButton("›", () => {
            motifTrack.scrollBy({ left: 240, behavior: "smooth" });
        });
        nextMotifs.classList.add("xa-store-crop-motif-nav");
        nextMotifs.setAttribute("aria-label", "Weitere Motive anzeigen");
        const motifRail = xaElement("div", "xa-store-crop-motif-rail");
        motifRail.append(previousMotifs, motifTrack, nextMotifs);
        motifList.setAttribute("aria-label", "Motive im Set auswählen");
        let visibleMotifId;
        const renderMotifs = () => {
            const scrollLeft = motifTrack.scrollLeft;
            motifTrack.replaceChildren();
            motifList.replaceChildren(xaElement("h3", "", "Motive im Set"), motifRail);
            this.selected.forEach((entry) => {
                const displayed = entry.id === originalMotif.id
                    ? workingMotif
                    : sessions.get(entry.id)?.motif || entry;
                const button = xaButton("", () => {
                    void (async () => {
                        if (entry.id === originalMotif.id)
                            return;
                        persistMotif();
                        originalMotif = entry;
                        const saved = sessions.get(entry.id);
                        workingMotif = saved?.motif || entry;
                        tiles = this.config.tiles.filter((item) => item.imageId === entry.imageId);
                        workingTiles = structuredClone(saved?.tiles || tiles);
                        common = structuredClone(saved?.common ||
                            this.common.get(entry.imageId) || {
                            id: "common",
                            imageId: entry.imageId,
                            rotationDeg: 0,
                            crop: fitCrop(entry.widthPx, entry.heightPx, this.config.tileSizeMm.width, this.config.tileSizeMm.height, 0),
                        });
                        tile = structuredClone(workingTiles[0] || common);
                        ready = false;
                        this.setImage(image, workingMotif, "working");
                        draw();
                    })();
                });
                button.classList.add("xa-store-crop-motif");
                const quantity = this.config.tiles.filter((item) => item.imageId === entry.imageId).length;
                button.setAttribute("aria-label", `${displayed.name}, Anzahl ${quantity}, zuschneiden`);
                button.title = displayed.name;
                button.setAttribute("aria-pressed", String(entry.id === originalMotif.id));
                const preview = xaElement("img");
                this.setImage(preview, displayed, "thumbnail");
                preview.alt = "";
                const badge = xaElement("span", "xa-store-crop-motif-quantity", `${quantity}×`);
                const selected = xaElement("span", "xa-store-crop-motif-selected", "✓");
                selected.setAttribute("aria-hidden", "true");
                button.append(preview, badge, selected);
                motifTrack.append(button);
            });
            motifTrack.scrollLeft = scrollLeft;
            if (visibleMotifId !== originalMotif.id && motifTrack.clientWidth > 0) {
                const active = motifTrack.querySelector('[aria-pressed="true"]');
                if (active) {
                    motifTrack.scrollLeft +=
                        active.getBoundingClientRect().left -
                            motifTrack.getBoundingClientRect().left -
                            (motifTrack.clientWidth - active.offsetWidth) / 2;
                    visibleMotifId = originalMotif.id;
                }
            }
        };
        const draw = () => {
            if (ready) {
                editor.render();
            }
            subtitle.textContent = workingMotif.name + " · Anzahl " + tiles.length;
            renderMotifs();
        };
        image.onload = () => {
            ready = true;
            draw();
        };
        this.setImage(image, motif, "working");
        const swapButton = xaButton("Motiv tauschen", () => {
            const savedBody = Array.from(body.childNodes);
            const savedFooter = Array.from(footer.childNodes);
            const previousTitle = title.textContent;
            body.replaceChildren();
            footer.replaceChildren();
            title.textContent = "Motiv tauschen";
            subtitle.hidden = true;
            dialog.classList.remove("xa-store-crop-dialog");
            const restore = () => {
                body.replaceChildren(...savedBody);
                footer.replaceChildren(...savedFooter);
                title.textContent = previousTitle;
                subtitle.hidden = false;
                dialog.classList.add("xa-store-crop-dialog");
                draw();
            };
            void this.library(workingMotif, (replacement) => {
                void (async () => {
                    workingMotif = replacement;
                    const crop = fitCrop(replacement.widthPx, replacement.heightPx, this.config.tileSizeMm.width, this.config.tileSizeMm.height, 0);
                    workingTiles = workingTiles.map((item) => {
                        return {
                            ...item,
                            imageId: replacement.imageId,
                            rotationDeg: 0,
                            crop: structuredClone(crop),
                        };
                    });
                    common = { ...common, imageId: replacement.imageId, rotationDeg: 0, crop };
                    tile = structuredClone(workingTiles[0] || common);
                    subtitle.textContent = `${replacement.name} · ${tiles.length} Aufkleber`;
                    ready = false;
                    this.setImage(image, replacement, "working");
                })();
            }, { dialog, body, footer, restore });
        });
        swapButton.classList.add("xa-store-crop-swap");
        const stage = xaElement("div", "xa-store-crop-stage");
        stage.append(editor.root, xaElement("p", "xa-store-crop-dimensions", `${this.config.tileSizeMm.width} × ${this.config.tileSizeMm.height} mm`), xaElement("p", "xa-store-crop-help", "Ecken ziehen: Ausschnitt ändern · Ausschnitt ziehen: verschieben"));
        const controls = xaElement("div", "xa-store-crop-controls");
        const tools = xaElement("div", "xa-store-crop-tools");
        for (const direction of [-90, 90]) {
            const button = xaButton("90°", () => {
                const previous = tile.crop;
                const base = fitCrop(workingMotif.widthPx, workingMotif.heightPx, this.config.tileSizeMm.width, this.config.tileSizeMm.height, tile.rotationDeg);
                const zoom = Math.max(1, base.width / previous.width);
                const cx = previous.x + previous.width / 2;
                const cy = previous.y + previous.height / 2;
                tile.rotationDeg = ((tile.rotationDeg + direction + 360) %
                    360);
                tile.crop = fitCrop(workingMotif.widthPx, workingMotif.heightPx, this.config.tileSizeMm.width, this.config.tileSizeMm.height, tile.rotationDeg, direction > 0 ? 1 - cy : cy, direction > 0 ? cx : 1 - cx, zoom);
                draw();
            });
            button.setAttribute("aria-label", direction < 0 ? "90° nach links drehen" : "90° nach rechts drehen");
            button.prepend(xaIcon(direction < 0 ? "reset" : "rotate"));
            tools.append(button);
        }
        const reset = xaButton("Zurücksetzen", () => {
            tile.rotationDeg = 0;
            tile.crop = fitCrop(workingMotif.widthPx, workingMotif.heightPx, this.config.tileSizeMm.width, this.config.tileSizeMm.height, 0);
            draw();
        });
        reset.prepend(xaIcon("reset"));
        swapButton.prepend(xaIcon("swap"));
        tools.append(reset, swapButton);
        const mobileSwap = xaButton("Motiv tauschen", () => swapButton.click());
        mobileSwap.classList.add("xa-store-crop-swap");
        controls.append(mobileSwap);
        editor.root.append(tools);
        const layout = xaElement("div", "xa-store-crop-layout");
        const workspace = xaElement("div", "xa-store-crop-workspace");
        workspace.append(stage);
        layout.append(workspace, motifList);
        headerText.append(subtitle);
        body.append(layout, controls);
        subtitle.textContent = workingMotif.name + " · Anzahl " + tiles.length;
        renderMotifs();
        const remove = xaButton("Motiv löschen", () => {
            void (async () => {
                const removedMotif = originalMotif;
                if (!(await xaConfirm(`„${workingMotif.name}“ wird mit allen zugehörigen Aufklebern aus deinem Set entfernt.`, {
                    destructive: true,
                    detail: workingMotif.isUpload
                        ? "Dein Bild wird aus diesem Set entfernt."
                        : "Die Motivbibliothek bleibt unverändert.",
                }))) {
                    return;
                }
                this.selected = this.selected.filter((item) => item.id !== removedMotif.id);
                this.config.tiles = this.config.tiles.filter((item) => item.imageId !== removedMotif.imageId);
                this.config.images = this.config.images.filter((item) => item.id !== removedMotif.imageId);
                this.common.delete(removedMotif.imageId);
                dialog.close();
                this.render();
                this.changed();
            })();
        });
        remove.prepend(xaIcon("trash"));
        remove.classList.add("xa-store-footer-start", "xa-store-delete-action");
        footer.append(remove);
        footer.append(xaButton("Abbrechen", () => dialog.close()), xaButton("Ausschnitt übernehmen", () => {
            void (async () => {
                persistMotif();
                for (const session of sessions.values()) {
                    if (session.motif.id !== session.original.id) {
                        this.selected = this.selected.filter((item) => item.id !== session.original.id);
                        this.addMotif(session.motif);
                    }
                    this.common.set(session.motif.imageId, session.common);
                    this.config.tiles = this.config.tiles.map((item) => session.tiles.find((edited) => edited.id === item.id) || item);
                }
                dialog.close();
                this.render();
                this.changed();
            })();
        }, true));
    }
}
customElements.define("xa-fliesen-generator", XaFliesenGenerator);
